/**
 * Contact editorial — subject select, phone dial code, optional photo (compressed).
 * Shopify contact forms reject native file inputs; we compress to JPEG data-URL
 * and send it in a text field the merchant receives by email.
 */
class ContactSelect extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-contact-select-trigger]');
    this.list = this.querySelector('[data-contact-select-list]');
    this.hiddenInput = this.querySelector('input[type="hidden"]');
    this.labelEl = this.querySelector('[data-contact-select-label]');
    if (!this.button || !this.list || !this.hiddenInput) return;

    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.#toggle();
    });

    this.list.addEventListener('click', (e) => {
      const option = e.target.closest('[data-value]');
      if (!(option instanceof HTMLElement)) return;
      this.#choose(option.dataset.value || '', option.textContent?.trim() || '');
    });

    document.addEventListener('click', this.#onDocClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocClick);
  }

  #onDocClick = (event) => {
    if (!this.contains(event.target)) this.#close();
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      this.#close();
      this.button?.focus();
    }
  };

  #toggle() {
    const open = this.getAttribute('open') != null;
    if (open) this.#close();
    else this.#open();
  }

  #open() {
    this.setAttribute('open', '');
    this.button?.setAttribute('aria-expanded', 'true');
    this.list?.removeAttribute('hidden');
  }

  #close() {
    this.removeAttribute('open');
    this.button?.setAttribute('aria-expanded', 'false');
    this.list?.setAttribute('hidden', '');
  }

  #choose(value, label) {
    this.hiddenInput.value = value;
    if (this.labelEl) this.labelEl.textContent = label;
    this.button?.classList.toggle('is-placeholder', !value);
    this.#close();
    this.button?.focus();
  }
}

class ContactPhone extends HTMLElement {
  connectedCallback() {
    this.dialSelect = this.querySelector('[data-contact-phone-dial]');
    this.dialLabel = this.querySelector('[data-contact-phone-dial-label]');
    this.numberInput = this.querySelector('[data-contact-phone-number]');
    this.hiddenInput = this.querySelector('input[type="hidden"][data-contact-phone-value]');
    if (!this.dialSelect || !this.numberInput || !this.hiddenInput) return;

    const sync = () => this.#sync();
    this.dialSelect.addEventListener('change', () => {
      this.#syncDialLabel();
      sync();
    });
    this.numberInput.addEventListener('input', sync);
    this.closest('form')?.addEventListener('submit', sync);
    this.#syncDialLabel();
    sync();
  }

  #syncDialLabel() {
    if (!this.dialLabel) return;
    const label = this.dialSelect.options[this.dialSelect.selectedIndex]?.textContent?.trim() || '+33';
    this.dialLabel.textContent = label;
  }

  #sync() {
    const dial = this.dialSelect.value || '';
    const raw = (this.numberInput.value || '').replace(/\s+/g, '');
    if (!raw) {
      this.hiddenInput.value = '';
      return;
    }
    const national = raw.replace(/^0+/, '');
    this.hiddenInput.value = `${dial}${national}`;
  }
}

class ContactImage extends HTMLElement {
  static MAX_INPUT_BYTES = 8 * 1024 * 1024;
  static MAX_OUTPUT_CHARS = 280000; // ~210 KB base64 — keeps Shopify email payloads safe
  static MAX_EDGE = 1280;

  #busy = false;

  connectedCallback() {
    this.input = this.querySelector('[data-contact-image-input]');
    this.payload = this.querySelector('[data-contact-image-payload]');
    this.filename = this.querySelector('[data-contact-image-filename]');
    this.preview = this.querySelector('[data-contact-image-preview]');
    this.thumb = this.querySelector('[data-contact-image-thumb]');
    this.nameEl = this.querySelector('[data-contact-image-name]');
    this.errorEl = this.querySelector('[data-contact-image-error]');
    this.removeBtn = this.querySelector('[data-contact-image-remove]');
    this.trigger = this.querySelector('[data-contact-image-trigger]');
    if (!this.input || !this.payload) return;

    this.input.addEventListener('change', () => this.#onFile());
    this.removeBtn?.addEventListener('click', () => this.#clear());
    this.trigger?.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.#busy) return;
      this.input?.click();
    });
    this.closest('form')?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.closest('form')?.removeEventListener('submit', this.#onSubmit);
  }

  #onSubmit = (event) => {
    if (this.#busy) {
      event.preventDefault();
      return;
    }
  };

  async #onFile() {
    const file = this.input.files?.[0];
    this.#setError('');
    if (!file) {
      this.#clear();
      return;
    }

    if (!file.type.startsWith('image/') && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) {
      this.#setError(this.#msg('type'));
      this.#clear(false);
      return;
    }

    if (file.size > ContactImage.MAX_INPUT_BYTES) {
      this.#setError(this.#msg('size'));
      this.#clear(false);
      return;
    }

    this.#busy = true;
    this.trigger?.setAttribute('aria-busy', 'true');

    try {
      const dataUrl = await this.#compress(file);
      if (dataUrl.length > ContactImage.MAX_OUTPUT_CHARS) {
        this.#setError(this.#msg('heavy'));
        this.#clear(false);
        return;
      }
      this.payload.value = dataUrl;
      if (this.filename) this.filename.value = file.name;
      if (this.thumb) this.thumb.src = dataUrl;
      if (this.nameEl) this.nameEl.textContent = file.name;
      this.preview?.removeAttribute('hidden');
      this.trigger?.setAttribute('hidden', '');
    } catch (error) {
      console.warn('[contact-image]', error);
      this.#setError(this.#msg('fail'));
      this.#clear(false);
    } finally {
      this.#busy = false;
      this.trigger?.removeAttribute('aria-busy');
    }
  }

  #clear(resetInput = true) {
    this.payload.value = '';
    if (this.filename) this.filename.value = '';
    if (this.thumb) this.thumb.removeAttribute('src');
    if (this.nameEl) this.nameEl.textContent = '';
    this.preview?.setAttribute('hidden', '');
    this.trigger?.removeAttribute('hidden');
    if (resetInput) this.input.value = '';
  }

  #setError(message) {
    if (!this.errorEl) return;
    if (!message) {
      this.errorEl.textContent = '';
      this.errorEl.setAttribute('hidden', '');
      return;
    }
    this.errorEl.textContent = message;
    this.errorEl.removeAttribute('hidden');
  }

  #msg(key) {
    const lang = (document.documentElement.lang || 'fr').slice(0, 2);
    const dict = {
      fr: {
        type: 'Formats acceptés : JPG, PNG ou WebP.',
        size: 'Image trop lourde (2 Mo max).',
        heavy: 'Image encore trop lourde après compression. Essayez une image plus légère, ou envoyez-la via WhatsApp.',
        fail: 'Impossible de lire cette image. Réessayez ou utilisez WhatsApp.',
      },
      en: {
        type: 'Accepted formats: JPG, PNG or WebP.',
        size: 'Image too large (2 MB max).',
        heavy: 'Image still too large after compression. Try a lighter image, or send it via WhatsApp.',
        fail: 'Could not read this image. Try again or use WhatsApp.',
      },
      de: {
        type: 'Akzeptierte Formate: JPG, PNG oder WebP.',
        size: 'Bild zu groß (max. 2 MB).',
        heavy: 'Bild nach der Kompression noch zu groß. Bitte leichteres Bild oder WhatsApp nutzen.',
        fail: 'Bild konnte nicht gelesen werden. Erneut versuchen oder WhatsApp nutzen.',
      },
      es: {
        type: 'Formatos aceptados: JPG, PNG o WebP.',
        size: 'Imagen demasiado pesada (máx. 2 MB).',
        heavy: 'La imagen sigue siendo demasiado pesada. Pruebe una más ligera o envíela por WhatsApp.',
        fail: 'No se pudo leer la imagen. Inténtelo de nuevo o use WhatsApp.',
      },
      ar: {
        type: 'الصيغ المقبولة: JPG أو PNG أو WebP.',
        size: 'الصورة كبيرة جداً (بحد أقصى 2 ميغابايت).',
        heavy: 'الصورة ما زالت كبيرة بعد الضغط. جرّبوا صورة أخف أو أرسلوها عبر واتساب.',
        fail: 'تعذّر قراءة الصورة. أعيدوا المحاولة أو استخدموا واتساب.',
      },
    };
    return (dict[lang] || dict.fr)[key] || dict.fr[key];
  }

  /**
   * @param {File} file
   * @returns {Promise<string>}
   */
  #compress(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, ContactImage.MAX_EDGE / Math.max(img.width, img.height));
        const width = Math.max(1, Math.round(img.width * scale));
        const height = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('canvas'));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        let quality = 0.72;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        while (dataUrl.length > ContactImage.MAX_OUTPUT_CHARS && quality > 0.4) {
          quality -= 0.08;
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }
        resolve(dataUrl);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('load'));
      };
      img.src = url;
    });
  }
}

if (!customElements.get('contact-select')) {
  customElements.define('contact-select', ContactSelect);
}
if (!customElements.get('contact-phone')) {
  customElements.define('contact-phone', ContactPhone);
}
if (!customElements.get('contact-image')) {
  customElements.define('contact-image', ContactImage);
}
